import { toTitleCase } from '@dispatch/shared';
import { isRunningInJest } from '@dispatch/testing';
import { Button, Card, CardContent, CardHeader, Stack } from '@mui/material';
import {
  GridColumns,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';

const tableStyleOverrides = {
  '& .MuiDataGrid-iconSeparator': {
    display: 'none',
  },
  '&.MuiDataGrid-root': {
    outline: 'none !important',
    border: 'none !important',
    backgroundColor: '#ffffff',
  },
  '&.MuiDataGrid-root .MuiDataGrid-cell': {
    outline: 'none !important',
    fontStyle: 'normal',
  },
  '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer': {
    padding: 0,
  },
  '.MuiDataGrid-columnHeaderTitle': {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.17px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '.MuiTablePagination-root .MuiTablePagination-selectLabel': {
    color: 'rgba(0, 0, 0, 0.6) !important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-actionsCell .MuiSvgIcon-root': {
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '24px',
  },
  '.MuiDataGrid-overlay': {
    height: 'auto !important',
  },
  '.MuiDataGrid-cell': {
    borderBottom: 'none !important',
  },
  '.MuiDataGrid-row': {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  '.MuiDataGrid-row:last-child': {
    borderBottom: 'none !important',
  },
};

export function AdminDashboardTable<T>({
  type,
  data,
  columns = [],
  buttonTitle,
  identifier,
  handleClick,
  isLoading,
}: {
  type: string;
  buttonTitle?: string;
  columns?: GridColumns;
  data?: T[];
  handleClick?: () => void;
  identifier: string;
  isLoading?: boolean;
}) {
  const title = toTitleCase(type);

  const CustomToolbar: React.FunctionComponent<{
    setFilterButtonEl: React.Dispatch<
      React.SetStateAction<HTMLButtonElement | null>
    >;
  }> = () => (
    <GridToolbarContainer>
      <GridToolbarQuickFilter placeholder={`Search ${title}`} />
    </GridToolbarContainer>
  );

  return (
    <Card>
      <CardHeader
        title={`${title}s`}
        sx={{
          '.MuiCardHeader-title': {
            fontSize: '18px',
          },
        }}
        action={
          <Button
            onClick={() => handleClick?.()}
            variant="outlined"
            size="small"
          >
            {buttonTitle || `Add ${title}`}
          </Button>
        }
      />
      <CardContent>
        <div style={{ height: '272px' }}>
          <DataGridPro
            disableSelectionOnClick={true}
            loading={isLoading}
            disableVirtualization={isRunningInJest()}
            rows={data || []}
            getRowId={(row) => `${row[identifier]}`}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  No {type}s
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  Filter Returned No {type}s
                </Stack>
              ),
            }}
            rowHeight={32}
            columns={columns}
            pageSize={5}
            pagination
            rowsPerPageOptions={[5]}
            sx={{ ...tableStyleOverrides }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
